import { useState, useMemo, useRef } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { FileText, Plus, AlertTriangle, Clock, ArrowDownCircle, ArrowUpCircle } from 'lucide-react';
import { CardStats, Card, ExportDropdown } from '@components/ui';
import { useExportExcel, useExportPDF } from '@hooks';
import {
    mockChecks,
    checkTypes,
    checkStatuses,
    banks,
    getCheckStats,
    getOverdueChecks,
    getBankLabel,
} from '@mocks/cekler';
import clsx from 'clsx';
import CeklerTablosu from './CeklerTablosu';
import CekModal from './CekModal';

const ITEMS_PER_PAGE = 10;

export default function Checks() {
    const { selectedConstruction, showAllConstructions } = useSelector((state) => state.construction);

    const [typeFilter, setTypeFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [currentPage, setCurrentPage] = useState(1);
    const [showModal, setShowModal] = useState(false);
    const [selectedCheck, setSelectedCheck] = useState(null);
    const [isEdit, setIsEdit] = useState(false);

    // Export hooks
    const { exportToExcel, isExporting: isExportingExcel } = useExportExcel();
    const { exportToPDF, isExporting: isExportingPDF } = useExportPDF();
    const tableRef = useRef(null);

    // Filtrelenmiş çekler
    const filteredChecks = useMemo(() => {
        let result = mockChecks;
        if (typeFilter !== 'all') {
            result = result.filter(c => c.type === typeFilter);
        }
        if (statusFilter !== 'all') {
            result = result.filter(c => c.status === statusFilter);
        }
        // Vade tarihine göre sırala
        return result.sort((a, b) => a.dueDate.localeCompare(b.dueDate));
    }, [typeFilter, statusFilter]);

    const stats = getCheckStats(mockChecks);
    const overdueChecks = getOverdueChecks();

    // Pagination
    const totalPages = Math.ceil(filteredChecks.length / ITEMS_PER_PAGE);
    const paginatedChecks = useMemo(() => {
        const start = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredChecks.slice(start, start + ITEMS_PER_PAGE);
    }, [filteredChecks, currentPage]);

    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('tr-TR', {
            style: 'currency',
            currency: 'TRY',
            minimumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleDateString('tr-TR', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
        });
    };

    const getStatusBadge = (status) => {
        return checkStatuses.find(s => s.key === status) || { label: status, color: 'bg-neutral-400' };
    };

    // Vade kontrolü
    const getDueDateClass = (dueDate, status) => {
        if (status !== 'bekliyor') return '';
        const today = new Date().toISOString().split('T')[0];
        const nextWeek = new Date();
        nextWeek.setDate(nextWeek.getDate() + 7);
        const nextWeekStr = nextWeek.toISOString().split('T')[0];

        if (dueDate < today) return 'text-error font-bold';
        if (dueDate <= nextWeekStr) return 'text-warning font-semibold';
        return '';
    };

    const handleAddClick = () => {
        setSelectedCheck(null);
        setIsEdit(false);
        setShowModal(true);
    };

    const handleRowClick = (check) => {
        setSelectedCheck(check);
        setIsEdit(true);
        setShowModal(true);
    };

    const handleSubmit = (formData) => {
        console.log("Çek Kaydedildi:", formData);
        toast.success(isEdit ? 'Çek başarıyla güncellendi' : 'Yeni çek sisteme eklendi');
        setShowModal(false);
    };

    return (
        <div className="p-4 md:p-6 space-y-6">
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-text-primary">Çek Takibi / Finansal Portföy</h1>
                    <p className="text-text-tertiary mt-1">
                        {showAllConstructions
                            ? 'Tüm inşaatlar - Alınan ve verilen kıymetli evrak takibi'
                            : `${selectedConstruction?.name || ''} - Çek takibi`}
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <ExportDropdown
                        onExportExcel={() => exportToExcel({
                            data: filteredChecks,
                            columns: [
                                { header: 'Çek No', key: 'checkNo' },
                                { header: 'Tür', key: 'type' },
                                { header: 'Kime/Kimden', key: 'type', format: (_, row) => row.type === 'alinan' ? row.customer : row.recipient },
                                { header: 'Banka', key: 'bank' },
                                { header: 'Vade Tarihi', key: 'dueDate', format: 'date' },
                                { header: 'Tutar', key: 'amount', format: 'currency' },
                                { header: 'Durum', key: 'status' },
                            ],
                            fileName: 'cekler_raporu',
                            sheetName: 'Çekler',
                        })}
                        onExportPDF={() => exportToPDF({
                            elementRef: tableRef,
                            fileName: 'cekler_raporu',
                            title: 'Çek Takibi Raporu',
                            orientation: 'landscape',
                        })}
                        isExporting={isExportingExcel || isExportingPDF}
                        disabled={filteredChecks.length === 0}
                    />
                    <button
                        onClick={handleAddClick}
                        className="flex items-center gap-2 px-4 py-2.5 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
                    >
                        <Plus size={18} />
                        <span className="font-medium">Yeni Çek Başvurusu</span>
                    </button>
                </div>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                <CardStats
                    title="Gelecek Borçlar (Verilen Çek)"
                    value={formatCurrency(stats.bekleyenVerilenToplam)}
                    icon={ArrowUpCircle}
                    color="error"
                    size="md"
                />
                <CardStats
                    title="Gelecek Tahsilat (Alınan Çek)"
                    value={formatCurrency(stats.bekleyenAlinanToplam)}
                    icon={ArrowDownCircle}
                    color="success"
                    size="md"
                />
                <CardStats
                    title="Gecikmiş Ödemeler"
                    value={`${stats.gecikmisAdet} adet`}
                    icon={AlertTriangle}
                    color="warning"
                    size="md"
                />
                <CardStats
                    title="Bu Hafta Vade"
                    value={`${stats.yaklasanAdet} adet`}
                    icon={Clock}
                    color="tertiary"
                    size="md"
                />
            </div>

            {/* Gecikmiş Çekler Uyarısı */}
            {overdueChecks.length > 0 && (
                <div className="bg-error/10 border border-error/30 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                        <AlertTriangle className="text-error" size={24} />
                        <div>
                            <h3 className="font-semibold text-error">Gecikmiş Çekler!</h3>
                            <p className="text-sm text-text-secondary">
                                {overdueChecks.length} adet çekin vadesi geçmiş. Zamanında müdahale edilmeli. Toplam: <span className="font-bold">{formatCurrency(stats.gecikmisToplam)}</span>
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3 md:gap-4">
                {/* Tür Filtreleri */}
                <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
                    <div className="flex items-center gap-1 sm:gap-2 bg-bg-secondary rounded-lg p-1 border border-border-light min-w-max">
                        <button
                            onClick={() => { setTypeFilter('all'); setCurrentPage(1); }}
                            className={clsx(
                                "px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors whitespace-nowrap min-h-[40px]",
                                typeFilter === 'all' ? "bg-primary text-white" : "text-text-secondary hover:bg-bg-hover"
                            )}
                        >
                            Tümü Portföy ({mockChecks.length})
                        </button>
                        <button
                            onClick={() => { setTypeFilter('alinan'); setCurrentPage(1); }}
                            className={clsx(
                                "px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors flex items-center gap-1 sm:gap-1.5 whitespace-nowrap min-h-[40px]",
                                typeFilter === 'alinan' ? "bg-success text-white" : "text-text-secondary hover:bg-bg-hover"
                            )}
                        >
                            <ArrowDownCircle size={14} />
                            <span>Alınan Çekler</span> ({stats.alinanAdet})
                        </button>
                        <button
                            onClick={() => { setTypeFilter('verilen'); setCurrentPage(1); }}
                            className={clsx(
                                "px-2.5 sm:px-3 py-2 text-xs sm:text-sm font-medium rounded-md transition-colors flex items-center gap-1 sm:gap-1.5 whitespace-nowrap min-h-[40px]",
                                typeFilter === 'verilen' ? "bg-error text-white" : "text-text-secondary hover:bg-bg-hover"
                            )}
                        >
                            <ArrowUpCircle size={14} />
                            <span>Verilen Çekler</span> ({stats.verilenAdet})
                        </button>
                    </div>
                </div>
                {/* Durum Filtresi */}
                <div className="flex items-center gap-2">
                    <span className="text-xs sm:text-sm text-text-tertiary">Durum:</span>
                    <select
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        className="flex-1 sm:flex-initial px-3 py-2 bg-bg-secondary border border-border-light rounded-lg text-text-primary text-sm min-h-[40px]"
                    >
                        <option value="all">Tümü</option>
                        {checkStatuses.map(status => (
                            <option key={status.key} value={status.key}>{status.label}</option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Desktop - Tablo Görünümü */}
            <div ref={tableRef}>
                <CeklerTablosu
                    paginatedChecks={paginatedChecks}
                    filteredChecks={filteredChecks}
                    currentPage={currentPage}
                    setCurrentPage={setCurrentPage}
                    totalPages={totalPages}
                    ITEMS_PER_PAGE={ITEMS_PER_PAGE}
                    onRowClick={handleRowClick}
                    getStatusBadge={getStatusBadge}
                    getDueDateClass={getDueDateClass}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                    getBankLabel={getBankLabel}
                />
            </div>

            {/* Modals */}
            <CekModal
                isOpen={showModal}
                onClose={() => setShowModal(false)}
                onSubmit={handleSubmit}
                selectedCheck={selectedCheck}
                isEdit={isEdit}
                banks={banks}
                checkStatuses={Object.fromEntries(checkStatuses.map(s => [s.key, s]))}
                checkTypes={checkTypes}
            />
        </div>
    );
}
